import {
  encodeFunctionData,
  parseAbi,
  parseUnits,
  type Abi,
  type AbiParameter,
} from 'viem';
import {
  accountTransactionSimulationRequestSchema,
  erc20TransferAbi,
  nativeTokenPseudoAddress,
  type AccountTransactionChildDraft,
  type AccountTransactionDraft,
  type NonEmptyArray,
  nonEmptyArray,
  type ParameterParsedValue,
} from '@mls-wallet/shared-types';
import type { TransactionBuilderParameterState } from './parameter-inputs/types';
import {
  TransactionTypeEnum,
  type ContractInteractionDetails,
  type TokenTransferDetails,
} from './useTransactionBuilderStore';

type BuildAccountTransactionChildDraftBaseInput = {
  policyId: string;
};

/** Requires exactly the detail payload selected by the transaction type. */
type BuildAccountTransactionChildDraftInput =
  | (BuildAccountTransactionChildDraftBaseInput & {
      transactionType: `${TransactionTypeEnum.TOKEN_TRANSFER}`;
      tokenTransferDetails: TokenTransferDetails;
      contractInteractionDetails?: never;
    })
  | (BuildAccountTransactionChildDraftBaseInput & {
      transactionType: `${TransactionTypeEnum.CONTRACT_INTERACTION}`;
      contractInteractionDetails: ContractInteractionDetails;
      tokenTransferDetails?: never;
    });

type BuildAccountTransactionDraftInput = Omit<
  AccountTransactionDraft,
  'children'
> & {
  children: NonEmptyArray<BuildAccountTransactionChildDraftInput>;
};

/** Collect parsed parameter values in ABI input order for calldata encoding. */
function getTransactionCalldataParamsArray(
  params: TransactionBuilderParameterState[],
  functionInputs: AbiParameter[]
): ParameterParsedValue[] {
  return functionInputs.map((_param, index) => {
    // Case: the builder has no state entry for this ABI parameter.
    if (!Object.prototype.hasOwnProperty.call(params, index)) {
      throw new Error(`Function param not found at index ${index}`);
    }

    const parsedValue = params[index]?.parsedValue;
    // Case: the state entry exists but has not produced an encodable value.
    if (parsedValue === undefined) {
      throw new Error(`Function param not found at index ${index}`);
    }

    return parsedValue;
  });
}

/** Render one ABI input as a human-readable Solidity type for viem. */
function buildFunctionType(input: AbiParameter): string {
  const arraySuffix = input.type.match(/(?:\[\d*\])+$/)?.[0] ?? '';
  const baseType = input.type.slice(0, input.type.length - arraySuffix.length);
  // Case: Non-tuple inputs or tuples without components retain their ABI type.
  if (baseType !== 'tuple' || !('components' in input) || !input.components) {
    return input.type;
  }

  const componentTypes = input.components
    .map((component) => buildFunctionType(component as AbiParameter))
    .join(',');
  return `(${componentTypes})${arraySuffix}`;
}

/** Build the single-function ABI declaration used to encode a draft call. */
function buildFunctionSignature(
  functionName: string,
  inputs: AbiParameter[]
): string {
  return `function ${functionName}(${inputs.map(buildFunctionType).join(',')})`;
}

/** Builds the parent and its ordered children together for creation and simulation. */
export function buildAccountTransactionDraft(
  input: BuildAccountTransactionDraftInput
): AccountTransactionDraft {
  return {
    multichainAccountId: input.multichainAccountId,
    networkId: input.networkId,
    description: input.description,
    children: nonEmptyArray(
      input.children.map(buildAccountTransactionChildDraft)
    ),
  };
}

/** Encodes only child fields; source account and network stay on the parent. */
export function buildAccountTransactionChildDraft(
  input: BuildAccountTransactionChildDraftInput
): AccountTransactionChildDraft {
  const baseDraft = {
    policyId: input.policyId,
  };

  // Case: encode a native or ERC-20 token transfer.
  if (input.transactionType === TransactionTypeEnum.TOKEN_TRANSFER) {
    const { asset, displayAmount, recipient } = input.tokenTransferDetails;
    // Case: The native-token pseudo-address encodes a value transfer, not ERC-20 calldata.
    if (
      asset.tokenAddress.toLowerCase() ===
      nativeTokenPseudoAddress.toLowerCase()
    ) {
      return {
        ...baseDraft,
        to: recipient.address,
        value: parseUnits(displayAmount, asset.decimals).toString(),
        data: '0x',
      };
    }

    return {
      ...baseDraft,
      to: asset.tokenAddress,
      value: '0',
      data: encodeFunctionData({
        abi: erc20TransferAbi,
        functionName: 'transfer',
        args: [
          recipient.address as `0x${string}`,
          parseUnits(displayAmount, asset.decimals),
        ],
      }),
      functionName: 'transfer',
      functionSignature:
        'function transfer(address to, uint256 amount) returns (bool)',
      functionInputs: [
        { name: 'to', type: 'address' },
        { name: 'amount', type: 'uint256' },
      ],
    };
  }

  const { contractInteractionDetails } = input;

  // Case: preserve an imported raw transaction without ABI re-encoding.
  if (contractInteractionDetails.rawTransaction) {
    return {
      ...baseDraft,
      to: contractInteractionDetails.rawTransaction.to,
      value: contractInteractionDetails.rawTransaction.value,
      data: contractInteractionDetails.rawTransaction.data,
    };
  }

  // Case: encode a contract call from its selected function and live parameters.
  if (contractInteractionDetails.selectedFunction) {
    const { selectedFunction } = contractInteractionDetails;
    const functionInputs = selectedFunction.inputs as AbiParameter[];
    const functionSignature = buildFunctionSignature(
      selectedFunction.name,
      functionInputs
    );
    const abi: Abi = parseAbi([functionSignature]);
    const args = getTransactionCalldataParamsArray(
      contractInteractionDetails.functionParameters ?? [],
      functionInputs
    );

    return {
      ...baseDraft,
      to: contractInteractionDetails.address,
      value: contractInteractionDetails.nativeTokenValue
        ? parseUnits(
            contractInteractionDetails.nativeTokenValue.value,
            contractInteractionDetails.nativeTokenValue.decimals
          ).toString()
        : '0',
      data: encodeFunctionData({
        abi,
        functionName: selectedFunction.name,
        args,
      }),
      functionName: selectedFunction.name,
      functionSignature,
      functionInputs: functionInputs.map((input) => ({
        name: input.name ?? '',
        type: input.type,
      })),
    };
  }

  throw new Error('Missing contract interaction details');
}

/** Validates simulation fields while retaining the complete creation draft. */
export function buildTransactionSimulationRequest(
  input: BuildAccountTransactionDraftInput
): AccountTransactionDraft {
  const draft = buildAccountTransactionDraft(input);
  accountTransactionSimulationRequestSchema.parse(draft);
  return draft;
}
